// @ts-check
// Regras puras das telas da retenção (contrato 10 de atendimento, seção 24.5): cores do risco, abas da tela Casos de
// retenção na URL, situação do próximo contato, validação do desfecho e do formulário de configuração. Sem Preact, para
// os testes do cliente e para o modo demonstração.
import { RETENTION_SIGNAL_KEYS } from '../../../shared/retention/retention-signals.js';
import { RETENTION_SETTINGS_LIMITS, findCadenceProblem } from '../../../shared/retention/retention-settings.js';

export const RETENTION_TABS = Object.freeze(/** @type {const} */ (['cases', 'households', 'signals']));
/** @typedef {typeof RETENTION_TABS[number]} RetentionTab */

export const RISK_COLORS = Object.freeze(/** @type {Record<string, string>} */ ({ LOW: 'green', MEDIUM: 'orange', HIGH: 'red' }));
export const OUTCOME_COLORS = Object.freeze(/** @type {Record<string, string>} */ ({ MONITORING: 'sky', STAYED: 'green', LEFT: 'red' }));

/** Aba da URL (`?tab=`), com "Casos abertos" como padrão. @param {string | null | undefined} value @returns {RetentionTab} */
export const readRetentionTab = (value) => (RETENTION_TABS.includes(/** @type {any} */ (value)) ? /** @type {RetentionTab} */ (value) : 'cases');

/**
 * Situação do próximo contato do caso: atrasado, hoje, nos próximos dias ou sem data.
 * @param {string | null | undefined} nextContactAt @param {Date} [now]
 * @returns {'NONE' | 'OVERDUE' | 'TODAY' | 'UPCOMING'}
 */
export function nextContactState(nextContactAt, now = new Date()) {
  if (!nextContactAt) return 'NONE';
  const due = new Date(nextContactAt);
  if (Number.isNaN(due.getTime())) return 'NONE';
  if (due.getTime() < now.getTime()) {
    const sameDay = due.toDateString() === now.toDateString();
    return sameDay ? 'TODAY' : 'OVERDUE';
  }
  return due.toDateString() === now.toDateString() ? 'TODAY' : 'UPCOMING';
}

/**
 * Problema do formulário do desfecho antes de mandar (o servidor confere de novo): Saiu pede o motivo e ao menos uma
 * matrícula; Em acompanhamento pede o próximo contato no futuro.
 * @param {{ outcome: string | null, exitReason?: string | null, enrollmentIds?: string[], nextContactAt?: string | null, hasEnrollments?: boolean }} form
 * @param {Date} [now]
 * @returns {'OUTCOME_REQUIRED' | 'EXIT_REASON_REQUIRED' | 'ENROLLMENTS_REQUIRED' | 'NEXT_CONTACT_REQUIRED' | null}
 */
export function findOutcomeProblem(form, now = new Date()) {
  if (!form.outcome || !['MONITORING', 'STAYED', 'LEFT'].includes(form.outcome)) return 'OUTCOME_REQUIRED';
  if (form.outcome === 'LEFT') {
    if (!form.exitReason) return 'EXIT_REASON_REQUIRED';
    if (form.hasEnrollments && (!form.enrollmentIds || form.enrollmentIds.length === 0)) return 'ENROLLMENTS_REQUIRED';
  }
  if (form.outcome === 'MONITORING') {
    const time = form.nextContactAt ? Date.parse(form.nextContactAt) : Number.NaN;
    if (Number.isNaN(time) || time <= now.getTime()) return 'NEXT_CONTACT_REQUIRED';
  }
  return null;
}

/**
 * Cadência digitada ("1, 3, 7") em dias, ou o motivo do problema.
 * @param {string} text
 * @returns {{ days: number[], problem: null } | { days: null, problem: string }}
 */
export function parseCadenceText(text) {
  const parts = String(text ?? '').split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean);
  const days = parts.map(Number);
  if (days.some((day) => !Number.isInteger(day))) return { days: null, problem: 'CADENCE_DAY_OUT_OF_RANGE' };
  const problem = findCadenceProblem(days);
  return problem ? { days: null, problem } : { days, problem: null };
}

/** @param {ReadonlyArray<number>} days */
export const formatCadenceText = (days) => days.join(', ');

/**
 * Linhas dos pesos na ordem dos sinais, com o valor do rascunho.
 * @param {Record<string, number>} weights
 * @returns {Array<{ key: string, weight: number, isOff: boolean }>}
 */
export const weightRows = (weights) => RETENTION_SIGNAL_KEYS.map((key) => ({ key, weight: Number(weights[key] ?? 0), isOff: Number(weights[key] ?? 0) === 0 }));

/**
 * Diferença entre o rascunho e a configuração salva, para mandar só o que mudou.
 * @param {Record<string, any>} saved @param {Record<string, any>} draft
 */
export function settingsPatch(saved, draft) {
  /** @type {Record<string, any>} */
  const patch = {};
  for (const key of ['threshold', 'signalLifetimeDays', 'autoOpenCase', 'ownerRule', 'ownerTeamId', 'noReenrollmentResponseDays']) {
    if (draft[key] !== saved[key]) patch[key] = draft[key];
  }
  if (JSON.stringify(draft.cadenceDays) !== JSON.stringify(saved.cadenceDays)) patch.cadenceDays = draft.cadenceDays;
  /** @type {Record<string, number>} */
  const weights = {};
  for (const key of RETENTION_SIGNAL_KEYS) if (draft.weights?.[key] !== saved.weights?.[key]) weights[key] = draft.weights[key];
  if (Object.keys(weights).length > 0) patch.weights = weights;
  return patch;
}

/**
 * Problema de um número do formulário (fora do limite da configuração).
 * @param {'weight' | 'threshold' | 'signalLifetimeDays' | 'noReenrollmentResponseDays'} kind @param {unknown} value
 */
export function isSettingNumberValid(kind, value) {
  const limits = RETENTION_SETTINGS_LIMITS[kind];
  return Number.isInteger(value) && /** @type {number} */ (value) >= limits.minimum && /** @type {number} */ (value) <= limits.maximum;
}

/** Os sinais ativos primeiro (os mais novos antes), depois os dispensados e vencidos. @template {{ isActive: boolean, occurredAt: string }} T @param {T[]} signals */
export const sortSignals = (signals) => [...signals].sort((left, right) => Number(right.isActive) - Number(left.isActive) || right.occurredAt.localeCompare(left.occurredAt));
